import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { AnalyticsTable } from "@/components/analytics/AnalyticsTable";
import { AnalyticsFiltersComponent } from "@/components/analytics/AnalyticsFilters";
import {
  fetchEmailEvents,
  fetchMailboxesForFilter,
  AnalyticsFilters,
  PaginatedResponse,
  AnalyticsEmailEvent,
} from "@/services/analyticsService";

export default function AnalyticsPage() {
  const { tenantId } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<AnalyticsFilters>({});
  const pageSize = 25;

  const {
    data: emailEventsData,
    isLoading: isLoadingEvents,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: ["email-events", tenantId, currentPage, filters],
    queryFn: () => {
      if (!tenantId) throw new Error("No tenant ID");
      return fetchEmailEvents(tenantId, currentPage, pageSize, filters);
    },
    enabled: !!tenantId,
  });

  const { data: mailboxes = [], isLoading: isLoadingMailboxes } = useQuery({
    queryKey: ["mailboxes-filter", tenantId],
    queryFn: () => {
      if (!tenantId) throw new Error("No tenant ID");
      return fetchMailboxesForFilter(tenantId);
    },
    enabled: !!tenantId,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFiltersChange = (newFilters: AnalyticsFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleRefresh = () => {
    refetchEvents();
  };

  const defaultEmailEventsData: PaginatedResponse<AnalyticsEmailEvent> = {
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 0,
  };

  return (
    <>
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground">
          Insights and statistics for your email automation
        </p>
      </div>
      
      <div className="space-y-6">
        <AnalyticsFiltersComponent
          filters={filters}
          onFiltersChange={handleFiltersChange}
          mailboxes={mailboxes}
          isLoadingMailboxes={isLoadingMailboxes}
        />
        
        <div className="rounded-lg border shadow-sm">
          <div className="p-6">
            <h2 className="text-xl font-semibold">Email Events</h2>
            <p className="text-muted-foreground">
              Overview of all email automation events
            </p>
          </div>
          
          <div className="border-t p-6">
            <AnalyticsTable
              data={emailEventsData || defaultEmailEventsData}
              isLoading={isLoadingEvents}
              onPageChange={handlePageChange}
              onRefresh={handleRefresh}
            />
          </div>
        </div>
      </div>
    </>
  );
}